import React from "react";
import styles from "./DocumentCenterSelectionBanner.module.css";
import { CustomButton, CustomIconButton, icon } from "../../../../common/components";
import {
	environmentAllowed,
	Flags,
	isFeatureEnabled,
	isSegFeatureEnabled,
	SegFlags,
	translate,
} from "../../../../common/providers";
import BulkDateChangeButton from "./BulkDateChangeButton";

interface DocumentCenterSelectionBannerProps {
	selectionCount: number;
	onOpenDeleteDialog: () => void;
	hasInProgress: boolean;
	onOpenMoveDialog: () => void;
	hasActionPermission: boolean;
	hasSelectedDirectories: boolean;
	onDownload: () => void;
	hasUnsupported: boolean;
	onOpenTypeDialog: () => void;
	selectedDocuments: Array<number>;
}

const DocumentCenterSelectionBanner = ({
	selectionCount,
	onOpenDeleteDialog,
	hasInProgress,
	onOpenMoveDialog,
	hasActionPermission,
	hasSelectedDirectories,
	onDownload,
	hasUnsupported,
	onOpenTypeDialog,
	selectedDocuments,
}: DocumentCenterSelectionBannerProps) => {
	const hasDocWritePermission = isSegFeatureEnabled(SegFlags.DOCUMENT_CENTER_WRITING);
	return (
		<div className={styles.container}>
			<span className={styles.actions__selection}>
				{translate(
					selectionCount === 1 ? "document-center.table.single-selection" : "document-center.table.selection",
					{ selectionCount }
				)}
			</span>
			<div className={styles.actionContainer}>
				{environmentAllowed("veolia") && !hasSelectedDirectories && (
					<BulkDateChangeButton selectedDocuments={selectedDocuments} />
				)}
				<CustomIconButton
					btnClassName={styles.iconBtn}
					color="secondary"
					data-testid="documentCenter.downloadSelectionBtn"
					disabled={selectionCount === 0}
					icon={icon.faDownload}
					size="xs"
					tooltip={translate("document-center.table.actions.download")}
					variant="outlined"
					onClick={onDownload}
				/>
				{!hasInProgress && hasActionPermission && (
					<CustomIconButton
						btnClassName={styles.iconBtn}
						color="secondary"
						data-testid="documentCenter.moveSelectionBtn"
						disabled={selectionCount === 0 || !hasDocWritePermission}
						icon={icon.faArrowRight}
						iconColor={(!hasDocWritePermission && "var(--icon-disabled)") || "inherit"}
						size="xs"
						tooltip={
							(!hasDocWritePermission && translate("document-center.feature-right.update.disabled")) ||
							translate("document-center.table.actions.move")
						}
						variant="outlined"
						onClick={onOpenMoveDialog}
					/>
				)}
				{hasActionPermission && (
					<CustomIconButton
						btnClassName={styles.iconBtn}
						color="secondary"
						data-testid="documentCenter.deleteSelectionBtn"
						disabled={selectionCount === 0 || !hasDocWritePermission}
						icon={icon.faTrash}
						iconColor={(!hasDocWritePermission && "var(--icon-disabled)") || "inherit"}
						size="xs"
						tooltip={
							(!hasDocWritePermission && translate("document-center.feature-right.update.disabled")) ||
							translate("document-center.table.actions.delete")
						}
						variant="outlined"
						onClick={onOpenDeleteDialog}
					/>
				)}
				{isSegFeatureEnabled(SegFlags.DOC_TYPE_DELIVERABLE) &&
					isFeatureEnabled(Flags.WRITE_PLUGIN) &&
					!hasUnsupported && (
						<CustomButton
							className={styles.btn}
							color="secondary"
							data-testid="documentCenter.addTypeSelectionBtn"
							size="xs"
							startIcon={icon.faPlus}
							variant="outlined"
							onClick={onOpenTypeDialog}
						>
							{translate("document-center.table.actions.add-type")}
						</CustomButton>
					)}
			</div>
		</div>
	);
};

export default DocumentCenterSelectionBanner;
